import { AvatarStyleDefinition } from './avatarStyles';

/**
 * Builds an avatar URL from a style definition and option values.
 */
export function buildAvatarUrl(
  style: AvatarStyleDefinition,
  options: Record<string, string>,
): string {
  const params = new URLSearchParams();

  for (const cat of style.categories) {
    const value = options[cat.key] ?? cat.defaultValue;

    // Skip category if visibleWhen condition is not met
    if (cat.visibleWhen) {
      const conditionValue = options[cat.visibleWhen.key] ?? '';
      const allowed = cat.visibleWhen.value;
      if (
        Array.isArray(allowed)
          ? !allowed.includes(conditionValue)
          : conditionValue !== allowed
      ) {
        continue;
      }
    }

    // Skip category if hiddenWhen condition is met (hidden when key === value)
    if (cat.hiddenWhen) {
      const conditionValue = options[cat.hiddenWhen.key] ?? '';
      if (conditionValue === cat.hiddenWhen.value) {
        continue;
      }
    }

    // Handle probability toggles (e.g., facialHair=blank → facialHairProbability=0)
    const probabilityKey = style.probabilityToggles[cat.key];
    if (probabilityKey) {
      const isNone = value === 'blank' || value === 'none';
      params.set(probabilityKey, isNone ? '0' : '100');
      if (!isNone) {
        params.set(cat.key, value);
      }
    } else {
      params.set(cat.key, value);
    }
  }

  return `${style.baseUrl}?${params.toString()}`;
}

/**
 * Builds a fully static thumbnail URL that isolates a single category option.
 *
 * Only the target category is set. Every other category — components AND
 * colours — is explicitly emptied with `key[]`. The only exceptions are
 * required dependencies: colourFor parents and visibleWhen triggers.
 *
 * The URL is a pure function of (style, categoryKey, optionValue) — it never
 * reads from user options, so it is permanently cacheable.
 */
export function buildThumbnailUrl(
  style: AvatarStyleDefinition,
  _options: Record<string, string>,
  categoryKey: string,
  optionValue: string,
): string {
  const category = style.categories.find((c) => c.key === categoryKey);

  // Only the target and its hard dependencies are included
  const includedKeys = new Set<string>([categoryKey]);

  // Colour categories need their parent component visible
  if (category?.colourFor) includedKeys.add(category.colourFor);

  // visibleWhen dependencies (e.g. clothingGraphic needs clothing=graphicShirt)
  if (category?.visibleWhen) includedKeys.add(category.visibleWhen.key);

  const params = new URLSearchParams();

  // All values come from config — never from user options
  const valueOverrides = new Map<string, string>();
  valueOverrides.set(categoryKey, optionValue);
  if (category?.visibleWhen) {
    const triggerValue = category.visibleWhen.value;
    valueOverrides.set(
      category.visibleWhen.key,
      Array.isArray(triggerValue) ? triggerValue[0] : triggerValue,
    );
  }
  // For colourFor parents, ensure a visible (non-blank) value so the colour shows
  if (category?.colourFor) {
    const parentDefault = style.defaults[category.colourFor];
    if (parentDefault === 'blank' || parentDefault === 'none') {
      const parentCat = style.categories.find(
        (c) => c.key === category.colourFor,
      );
      const firstVisible = parentCat?.options.find(
        (o) => o !== 'blank' && o !== 'none',
      );
      if (firstVisible) valueOverrides.set(category.colourFor, firstVisible);
    }
  }

  for (const key of includedKeys) {
    const value = valueOverrides.get(key) ?? style.defaults[key];
    if (value === undefined) continue;

    const probabilityKey = style.probabilityToggles[key];
    if (probabilityKey) {
      const isNone = value === 'blank' || value === 'none';
      params.set(probabilityKey, isNone ? '0' : '100');
      if (!isNone) {
        params.set(key, value);
      }
    } else {
      params.set(key, value);
    }
  }

  // Strip non-included component categories with empty arrays;
  // non-included colour categories get their default value.
  const emptyArrayKeys: string[] = [];
  for (const cat of style.categories) {
    if (includedKeys.has(cat.key)) continue;
    if (
      cat.type === 'colour' &&
      cat.key !== 'skinColor' &&
      cat.key !== 'backgroundColor'
    ) {
      params.set(cat.key, cat.defaultValue);
    } else {
      emptyArrayKeys.push(cat.key);
      const probKey = style.probabilityToggles[cat.key];
      if (probKey) params.set(probKey, '0');
    }
  }

  // Strip extra non-category keys (e.g. nose)
  if (style.stripInThumbnails) {
    for (const key of style.stripInThumbnails) {
      if (!includedKeys.has(key)) emptyArrayKeys.push(key);
    }
  }

  // Apply zoom params if present
  if (category?.zoom) {
    params.set('scale', String(category.zoom.scale));
    params.set('translateX', String(category.zoom.translateX));
    params.set('translateY', String(category.zoom.translateY));
  }

  // Build URL — append empty array params manually (URLSearchParams can't produce `key[]` without `=`)
  const base = `${style.baseUrl}?${params.toString()}`;
  const emptyArrayStr = emptyArrayKeys
    .map((key) => `${encodeURIComponent(key)}[]`)
    .join('&');

  return emptyArrayStr ? `${base}&${emptyArrayStr}` : base;
}
